
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchBlockByHash } from '../services/apiService';
import useTitle from '../hooks/useTitle';
import {
  FaCube,
  FaHashtag,
  FaClock,
  FaCogs,
  FaLink,
  FaList,
  FaArrowLeft,
  FaArrowRight,
  FaChevronDown,
  FaChevronUp,
  FaWallet,
  FaCoins,
} from 'react-icons/fa';

const BlockHashPage = () => {
  useTitle("MENELIK - Block Hash");
  const { hash } = useParams();
  const [blockData, setBlockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const [expandedTx, setExpandedTx] = useState({});

  useEffect(() => {
    if (hash) {
      fetchBlockByHash(hash).then((data) => {
        setBlockData(data);
        setLoading(false);
      });
    }
  }, [hash]);

  if (loading) return <p className="text-center text-lg text-gray-500 my-8">Loading block data...</p>;
  if (!blockData) return <p className="text-center text-lg text-gray-500 my-8">Block not found.</p>;

  const paginatedTransactions = blockData.transactions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(blockData.transactions.length / rowsPerPage);

  const toggleExpand = (txid) => {
    setExpandedTx((prevState) => ({
      ...prevState,
      [txid]: !prevState[txid],
    }));
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 flex items-center text-gray-800">
        <FaCube className="mr-2 text-blue-600" /> Block Details
      </h1>

      <div className="bg-white shadow-lg rounded-lg p-6 mb-6 text-sm md:text-base lg:text-lg border border-gray-200">
        <div className="mb-4">
          <p className="flex items-center text-gray-700">
            <FaHashtag className="mr-2 text-gray-500" />
            <strong className="mr-2">Block Hash:</strong>
            <span className="break-all">{blockData.block_hash}</span>
          </p>
        </div>
        <div className="mb-4">
          <p className="flex items-center text-gray-700">
            <FaClock className="mr-2 text-gray-500" />
            <strong className="mr-2">Time:</strong>
            <span>{new Date(blockData.time).toLocaleString()}</span>
          </p>
        </div>
        <div className="mb-4">
          <p className="flex items-center text-gray-700">
            <FaCogs className="mr-2 text-gray-500" />
            <strong className="mr-2">Height:</strong>
            <span>{blockData.height}</span>
          </p>
        </div>
        <div className="mb-4">
          <p className="flex items-center text-gray-700">
            <FaCube className="mr-2 text-gray-500" />
            <strong className="mr-2">Merkle Root:</strong>
            <span className="break-all">{blockData.merkleroot}</span>
          </p>
        </div>
        <div className="mb-4">
          <p className="flex items-center text-gray-700">
            <FaLink className="mr-2 text-gray-500" />
            <strong className="mr-2">Previous Block Hash:</strong>
            {blockData.previousblockhash ? (
              <Link
                to={`/block-hash/${blockData.previousblockhash}`}
                className="text-blue-600 hover:underline break-all"
              >
                {blockData.previousblockhash}
              </Link>
            ) : (
              <span className="text-gray-500">N/A</span>
            )}
          </p>
        </div>
        <div>
          <p className="flex items-center text-gray-700">
            <FaLink className="mr-2 text-gray-500" />
            <strong className="mr-2">Next Block Hash:</strong>
            {blockData.nextblockhash ? (
              <Link
                to={`/block-hash/${blockData.nextblockhash}`}
                className="text-blue-600 hover:underline break-all"
              >
                {blockData.nextblockhash}
              </Link>
            ) : (
              <span className="text-gray-500">N/A</span>
            )}
          </p>
        </div>
      </div>

      {/* Transacciones */}
      <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-semibold mb-4 flex items-center text-gray-800">
          <FaList className="mr-2 text-green-600" /> Transactions
        </h2>
        {blockData.transactions.length > 0 ? (
          <>
            <table className="table-auto w-full bg-gray-50 rounded overflow-hidden">
              <thead>
                <tr className="bg-gray-200 text-gray-700">
                  <th className="p-3 border-b">TxID</th>
                  <th className="p-3 border-b">Recipients</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((tx, index) => (
                  <React.Fragment key={index}>
                    <tr className="hover:bg-gray-100">
                      <td className="p-3 border-b break-all">
                        <Link
                          to={`/txid/${tx.txid}`}
                          className="text-blue-600 hover:underline flex items-center"
                        >
                          <FaLink className="mr-2" />
                          {tx.txid}
                        </Link>
                      </td>
                      <td className="p-3 border-b">
                        <button
                          className={`${
                            expandedTx[tx.txid] ? 'bg-blue-600' : 'bg-blue-500'
                          } text-white px-4 py-2 rounded flex items-center hover:bg-blue-700 transition-colors`}
                          onClick={() => toggleExpand(tx.txid)}
                        >
                          {expandedTx[tx.txid] ? (
                            <>
                              <FaChevronUp className="mr-2" />
                              Collapse
                            </>
                          ) : (
                            <>
                              <FaChevronDown className="mr-2" />
                              Expand
                            </>
                          )}
                        </button>
                      </td>
                    </tr>

                    {/* Subtabla (Recipients) */}
                    {expandedTx[tx.txid] && tx.recipients.length > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan="2" className="p-3 border-b">
                          <table className="table-auto w-full bg-white rounded shadow-inner">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="p-3 border-b">Address</th>
                                <th className="p-3 border-b">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tx.recipients.map((recipient, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="p-3 border-b break-all">
                                    <div className="flex items-center">
                                      <FaWallet className="mr-2 text-gray-600" />
                                      <Link 
                                        to={`/address/${recipient.address}`} 
                                        className="text-blue-500 hover:underline"
                                      >
                                        {recipient.address}
                                      </Link>
                                    </div>
                                  </td>

                                  <td className="p-3 border-b">
                                    <div className="flex items-center">
                                      <FaCoins className="mr-2 text-yellow-600" />
                                      {recipient.value}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>

                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Paginador */}
            <div className="flex justify-between items-center mt-4">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded flex items-center disabled:opacity-50"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <FaArrowLeft className="mr-2" />
                Previous
              </button>
              <p className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </p>
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded flex items-center disabled:opacity-50"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
                <FaArrowRight className="ml-2" />
              </button>
            </div>
          </>
        ) : (
          <p className="text-gray-500">No transactions found for this block.</p>
        )}
      </div>
    </div>
  );
};

export default BlockHashPage;
